/**
 * Support Coverage Report — MeesterJay 4.1
 */
import { TAXONOMY, FamilyDefinition, FAMILY_BY_ID } from '../content';
import { FAMILY_SUPPORT_REGISTRY, FamilySupportEntry } from './familySupportRegistry';
import { familyRequiresVisual as requiresVisual } from '../governance';
import { type Question } from '../content';
import { debug, warn, error as logError } from '../utils/logger';

// ============================================================
// Types
// ============================================================

export interface FamilyCoverageStatus {
  familyId: string;
  module: string;
  tier: number;
  levelCode: string;
  description: string;
  hasQuestions: boolean;
  questionCount: number;
  hasExplanation: boolean;
  hasHints: boolean;
  hasVisual: boolean;
  requiresVisual: boolean;
  requiresHints: boolean;
  /** Overall status for this family */
  status: 'complete' | 'partial' | 'missing_support' | 'missing_questions' | 'empty';
}
export interface SupportCoverageReport {
  totalFamilies: number;
  supportedFamilies: number;
  missingSupport: string[];
  coveragePercent: number;
  byModule: Record<string, {
    total: number;
    supported: number;
    missing: string[];
  }>;

  /** Enhanced: per-family detailed status */
  familyStatuses: FamilyCoverageStatus[];
  /** Families with questions but no support entry */
  questionsWithoutSupport: string[];
  /** Support entries that have no questions */
  supportWithoutQuestions: string[];
  /** Families that require visuals but are missing them */
  missingRequiredVisuals: string[];
  /** Families that require hints but are missing them */
  missingRequiredHints: string[];
  /** Summary counts */
  summary: {
    complete: number;
    partial: number;
    missingSupport: number;
    missingQuestions: number;
    empty: number;
  };
}

/**
 * Generate a comprehensive coverage report.
 * Cross-checks taxonomy, questions, and support registry.
 */
export function generateSupportCoverageReport(questions?: Question[]): SupportCoverageReport {
  const totalFamilies = TAXONOMY.length;
  const missingSupport: string[] = [];
  let supportedFamilies = 0;
  const byModule: Record<string, {
    total: number;
    supported: number;
    missing: string[];
  }> = {};

  // Build question count map
  const questionCountByFamily = new Map<string, number>();
  if (questions) {
    for (const q of questions) {
      const fid = q.familyId;
      if (fid) {
        questionCountByFamily.set(fid, (questionCountByFamily.get(fid) || 0) + 1);
      }
    }
  }
  const familyStatuses: FamilyCoverageStatus[] = [];
  const questionsWithoutSupport: string[] = [];
  const supportWithoutQuestions: string[] = [];
  const missingRequiredVisuals: string[] = [];
  const missingRequiredHints: string[] = [];
  const summary = {
    complete: 0,
    partial: 0,
    missingSupport: 0,
    missingQuestions: 0,
    empty: 0
  };
  TAXONOMY.forEach(family => {
    const moduleKey = family.module;
    if (!byModule[moduleKey]) {
      byModule[moduleKey] = {
        total: 0,
        supported: 0,
        missing: []
      };
    }
    byModule[moduleKey].total++;
    const entry = FAMILY_SUPPORT_REGISTRY[family.familyId];
    const qCount = questionCountByFamily.get(family.familyId) || 0;
    const hasQuestions = qCount > 0;
    const hasExplanation = !!entry?.defaultExplanation;
    const hasHints = !!entry?.hints?.length;
    const hasVisual = !!entry?.defaultVisual;

    // Use the new explicit visual rules
    const isVisualRequired = requiresVisual(family.familyId);
    const requiresHintsFlag = entry?.requiresHints ?? true; // default: most families need hints

    const hasAnySupport = hasExplanation || hasHints || hasVisual;

    // Determine status
    let status: FamilyCoverageStatus['status'] = 'empty';
    if (hasQuestions && hasAnySupport) {
      // Check if all required support is present
      const missingRequired = isVisualRequired && !hasVisual || requiresHintsFlag && !hasHints;
      status = missingRequired ? 'partial' : 'complete';
    } else if (hasQuestions && !hasAnySupport) {
      status = 'missing_support';
    } else if (!hasQuestions && hasAnySupport) {
      status = 'missing_questions';
    } else {
      status = 'empty';
    }

    // Track module-level support
    if (hasAnySupport) {
      supportedFamilies++;
      byModule[moduleKey].supported++;
    } else {
      missingSupport.push(family.familyId);
      byModule[moduleKey].missing.push(family.familyId);
    }

    // Track cross-reference issues
    if (hasQuestions && !hasAnySupport) {
      questionsWithoutSupport.push(family.familyId);
    }
    if (!hasQuestions && hasAnySupport) {
      supportWithoutQuestions.push(family.familyId);
    }
    if (isVisualRequired && !hasVisual) {
      missingRequiredVisuals.push(family.familyId);
    }
    if (requiresHintsFlag && !hasHints) {
      missingRequiredHints.push(family.familyId);
    }

    // Track summary
    summary[status === 'missing_support' ? 'missingSupport' : status === 'missing_questions' ? 'missingQuestions' : status]++;
    familyStatuses.push({
      familyId: family.familyId,
      module: family.module,
      tier: family.tier,
      levelCode: family.levelCode,
      description: family.description,
      hasQuestions,
      questionCount: qCount,
      hasExplanation,
      hasHints,
      hasVisual,
      requiresVisual: isVisualRequired,
      requiresHints: requiresHintsFlag,
      status
    });
  });
  return {
    totalFamilies,
    supportedFamilies,
    missingSupport,
    coveragePercent: totalFamilies > 0 ? Math.round(supportedFamilies / totalFamilies * 100) : 0,
    byModule,
    familyStatuses,
    questionsWithoutSupport,
    supportWithoutQuestions,
    missingRequiredVisuals,
    missingRequiredHints,
    summary
  };
}

/**
 * Fail-fast validation for required support.
 * Call at startup in dev/CI to catch missing support early.
 *
 * NOW THROWS ERRORS in development if critical support is missing.
 */
export function assertSupportCoverage(questions?: Question[]): void {
  const report = generateSupportCoverageReport(questions);
  const criticalIssues: string[] = [];
  if (report.questionsWithoutSupport.length > 0) {
    criticalIssues.push(`CRITICAL: ${report.questionsWithoutSupport.length} families have questions but NO support entry: ${report.questionsWithoutSupport.slice(0, 5).join(', ')}...`);
  }
  if (report.missingRequiredVisuals.length > 0) {
    criticalIssues.push(`CRITICAL: ${report.missingRequiredVisuals.length} families require visuals but have none: ${report.missingRequiredVisuals.slice(0, 5).join(', ')}...`);
  }
  if (report.missingRequiredHints.length > 0) {
    criticalIssues.push(`CRITICAL: ${report.missingRequiredHints.length} families require hints but have none: ${report.missingRequiredHints.slice(0, 5).join(', ')}...`);
  }
  if (criticalIssues.length > 0) {
    logError('🚨 SUPPORT COVERAGE ASSERTION FAILED:');
    criticalIssues.forEach(issue => logError(`  ❌ ${issue}`));
    if (process.env.NODE_ENV === 'development') {
      warn('⚠️ Fix these support gaps in src/support/familySupportRegistry.ts');
    }
  } else {
    debug('✅ Support Coverage Assertion Passed: All active families have required support.');
  }
}